<template>
    <div class="container mt-5">
      <h2 class="text-center mb-4">Admin Dashboard</h2>
      <h5 class="text-center mb-4">Version 0.10</h5>
      
      <!-- Team Registration Stats -->
      <div v-if="teamStats.length > 0" class="mb-4">
        <div class="alert alert-info">
          <h5 class="text-center mb-3">Today's Registration Stats</h5>
          <div v-for="stat in teamStats" :key="stat.location.id" class="text-center mb-2">
            <strong>{{ stat.teamCount }}</strong> teams registered for {{ formattedDate }} game at <strong>{{ stat.location.name }}</strong>
          </div>
        </div>
      </div>
      <div v-else-if="isLoading" class="text-center mb-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      
      <!-- Buttons to switch between different admin sections -->
      <div class="d-flex justify-content-center mb-4">
        <button @click="currentSection = 'locations'" class="btn btn-primary mx-2">
          Manage Locations
        </button>
        <button @click="currentSection = 'games'" class="btn btn-primary mx-2">
          Manage Games
        </button>
        <button @click="currentSection = 'roundTypes'" class="btn btn-primary mx-2">
          Create Round Type
        </button>
        <button @click="currentSection = 'previousGames'" class="btn btn-primary mx-2">
          Previous Games
        </button>
        <button @click="navigateToHostPage" class="btn btn-success mx-2">
          Host Page
        </button>
        <button @click="logout" class="btn btn-danger ms-4">
          Logout
        </button>
      </div>

      <!-- Conditionally Rendered Sections -->
      <div v-if="currentSection === 'locations'">
        <ManageLocations />
      </div>
  
      <div v-if="currentSection === 'games'">
        <ManageGames />
      </div>
  
      <div v-if="currentSection === 'roundTypes'">
        <CreateRoundType />
      </div>
      
      <div v-if="currentSection === 'previousGames'">
        <PreviousGamesLeaderboard />
      </div>
    </div>
    <footer></footer>
</template>
  
<script>
import ManageLocations from './ManageLocations.vue';
import ManageGames from './ManageGames.vue';
import CreateRoundType from './CreateRoundType.vue';
import PreviousGamesLeaderboard from './PreviousGamesLeaderboard.vue';
import { ref, onMounted } from 'vue';
import { db } from '@/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import moment from 'moment-timezone';

export default {
  name: 'AdminPage',
  components: {
    ManageLocations,
    ManageGames,
    CreateRoundType,
    PreviousGamesLeaderboard,
  },
  setup() {
    const currentSection = ref('locations');
    const activeLocations = ref([]);
    const teamStats = ref([]);
    const isLoading = ref(true);
    
    const getTodayDate = () => {
      return moment().tz('America/New_York').format('YYYY-MM-DD');
    };
    
    const formattedDate = moment().tz('America/New_York').format('MMM D, YYYY');
    
    const fetchTeamStats = async () => {
      isLoading.value = true;
      try {
        // Fetch active locations
        const locationsSnapshot = await getDocs(collection(db, 'locations'));
        activeLocations.value = locationsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(location => location.isActive);
        
        // For each active location, get today's game and count teams
        const stats = [];
        const todayDate = getTodayDate();
        
        for (const location of activeLocations.value) {
          // Find game for this location and today's date
          const gamesQuery = query(
            collection(db, 'games'),
            where('location', '==', location.id),
            where('date', '==', todayDate)
          );
          const gamesSnapshot = await getDocs(gamesQuery);
          
          if (!gamesSnapshot.empty) {
            const gameId = gamesSnapshot.docs[0].id;
            
            // Count teams for this game
            const teamsQuery = query(
              collection(db, 'teams'),
              where('location', '==', location.id),
              where('gameId', '==', gameId)
            );
            const teamsSnapshot = await getDocs(teamsQuery);
            
            stats.push({
              location: location,
              teamCount: teamsSnapshot.size,
              gameId: gameId
            });
          } else {
            // No game found for today at this location
            stats.push({
              location: location,
              teamCount: 0,
              gameId: null
            });
          }
        }
        
        teamStats.value = stats;
      } catch (error) {
        console.error('Error fetching team stats:', error);
      } finally {
        isLoading.value = false;
      }
    };
    
    onMounted(fetchTeamStats);
    
    const logout = () => {
      // Call the logout method from App.vue
      if (typeof this.$parent?.logout === 'function') {
        this.$parent.logout();
      }
    };
    
    const navigateToHostPage = () => {
      // Navigate to the HostPage
      window.location.href = '/Host';
    };
    
    return {
      currentSection,
      teamStats,
      isLoading,
      formattedDate,
      logout,
      navigateToHostPage
    };
  }
};
</script>

<style>
footer {
  height: 50px;
}
</style>
  